import { authFetchBinary, BASE_BACKEND_URL, fetchWithAuth } from '../../../auth'
import { useRef, useState } from 'react'

const useOcOrderActions = ({
  purchaseOrderFileId,
  purchaseOrderFileName,
  orderId
}) => {
  const reprocessAbortRef = useRef(null)

  const [loadingDownload, setLoadingDownload] = useState(false)
  const [loadingReprocess, setLoadingReprocess] = useState(false)
  const [error, setError] = useState(null)

  const extractFileName = (disposition) => {
    if (!disposition) return null

    const utf8Match = disposition.match(/filename\*\=UTF-8''([^;]+)/i)
    if (utf8Match) return decodeURIComponent(utf8Match[1])

    const asciiMatch = disposition.match(/filename="([^"]+)"/i)
    if (asciiMatch) return asciiMatch[1]

    const plainMatch = disposition.match(/filename=([^;]+)/i)
    if (plainMatch) return plainMatch[1].trim().replace(/^"|"$/g, '')

    return null
  }

  const buildDownloadUrl = (fileId) =>
    `${BASE_BACKEND_URL}/poc/purchase_order_files/${fileId}`.replace(
      /([^:]\/)\/+/g,
      '$1'
    )

  const isHtmlErrorPayload = (buffer) => {
    const preview = new TextDecoder().decode(new Uint8Array(buffer.slice(0, 32))).trimStart()

    return preview.startsWith('<!DOCTYPE') || preview.startsWith('<html')
  }

  const resolveFileName = (disposition) =>
    extractFileName(disposition) ||
    purchaseOrderFileName ||
    `archivo_${purchaseOrderFileId}`

  // DOWNLOAD

  const downloadPurchaseOrder = async () => {
    if (!purchaseOrderFileId) return

    try {
      setLoadingDownload(true)
      setError(null)

      const url = buildDownloadUrl(purchaseOrderFileId)

      const response = await authFetchBinary(url, {
        method: 'GET'
      })

      if (!response || !response.ok) {
        throw new Error(`Error descargando archivo (${response?.status ?? 'sin respuesta'})`)
      }

      const disposition = response.headers.get('Content-Disposition')
      const arrayBuffer = await response.arrayBuffer()

      if (isHtmlErrorPayload(arrayBuffer)) {
        throw new Error('La respuesta no es un archivo válido')
      }

      const contentType = response.headers.get('Content-Type')
      const blob = new Blob([arrayBuffer], { type: contentType.split(';')[0].trim() })
      const fileName = resolveFileName(disposition)

      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl)
      }, 5000)
    } catch (err) {
      console.error('[DOWNLOAD] Error:', err)
      alert('No se pudo descargar la orden de compra. Intente nuevamente.')
      setError(err)
    } finally {
      setLoadingDownload(false)
    }
  }

  // REPROCESS

  const reprocessPurchaseOrder = async () => {
    if (!orderId) return null

    reprocessAbortRef.current?.abort()
    const controller = new AbortController()
    reprocessAbortRef.current = controller

    try {
      setLoadingReprocess(true)
      setError(null)

      const response = await fetchWithAuth(
        `${BASE_BACKEND_URL}/poc/orders/${orderId}/reprocess`,
        { signal: controller.signal }
      )

      return response
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err)
        throw err
      }
    } finally {
      setLoadingReprocess(false)
      reprocessAbortRef.current = null
    }
  }

  return {
    downloadPurchaseOrder,
    reprocessPurchaseOrder,
    loadingDownload,
    loadingReprocess,
    error
  }
}

export default useOcOrderActions
